import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { LenderUser } from '../api/lenders';
import {
  getUsers,
  getUser,
  createUser,
  updateUser,
  updateUserStatus,
  getUserProducts,
  getUserEntitlements,
  type CreateUserRequest,
  type UpdateUserRequest,
  type UserStatus,
} from '../api/users';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });
}

export function useUser(urn: string) {
  return useQuery({
    queryKey: ['users', urn],
    queryFn: () => getUser(urn),
    enabled: !!urn,
  });
}

export function useUserProducts(urn: string) {
  return useQuery({
    queryKey: ['users', urn, 'products'],
    queryFn: () => getUserProducts(urn),
    enabled: !!urn,
  });
}

export function useUserEntitlements(urn: string) {
  return useQuery({
    queryKey: ['users', urn, 'entitlements'],
    queryFn: () => getUserEntitlements(urn),
    enabled: !!urn,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserRequest) => createUser(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, data }: { urn: string; data: UpdateUserRequest }) =>
      updateUser(urn, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, status }: { urn: string; status: UserStatus }) =>
      updateUserStatus(urn, status),
    onSuccess: (user, { urn }) => {
      qc.setQueryData(['users', urn], user);
      qc.invalidateQueries({ queryKey: ['users'], exact: true });
      const lenderId = /^urn:lender:([^:]+):user:/i.exec(urn)?.[1];
      if (lenderId) {
        const queryKey = ['lenders', `urn:lender:${lenderId}`, 'users'];
        qc.setQueryData<LenderUser[]>(queryKey, (current) => current?.map((item) =>
          item.urn === urn ? { ...item, status: user.status } : item));
        qc.invalidateQueries({ queryKey });
      }
    },
  });
}
